import React, { memo, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import { TextField, Grid } from '@material-ui/core'
import * as _ from 'lodash'
import Button from '@material-ui/core/Button'
import Fab from '@material-ui/core/Fab'
import classNames from 'classnames'

import { EUserOnboardingActionTypes } from 'models'
import { StoreValue } from 'store'
import styles from '../styles'
import { SearchSelect } from './'
import { UserOnboardingStateValue } from '../reducer'
import { useSkillCollectionQuery } from 'graphql/__generated__/graphql'

const useStyles = makeStyles(styles)

const Step2View: React.FunctionComponent<any> = props => {
	const {
		skills,
		values,
		error,
		handleNext,
		handleBack,
		userOnBoardDispatch,
	} = props
	const classes = useStyles()
	const { t } = useTranslation()

	const { refetch: refetchSkills } = useSkillCollectionQuery({
		skip: true,
	})
	const isNotValidStep = useCallback(() => {
		return _.isEmpty(skills) || _.isEmpty(values)
	}, [values, skills])

	const goToNextStep = useCallback(
		_error => {
			if (isNotValidStep()) {
				userOnBoardDispatch({
					type: EUserOnboardingActionTypes.setError,
					errors: {
						skills: _.isEmpty(skills),
						values: _.isEmpty(values),
					},
				})
				return
			}
			handleNext()
		},
		[handleNext, isNotValidStep, userOnBoardDispatch, values, skills],
	)
	const handleChangeMultiSkills = useCallback(
		value => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setSkills,
				skills: value ? value : [],
			})
		},
		[userOnBoardDispatch],
	)

	const handleChangeValues = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setValues,
				value: event.target.value,
			})
		},
		[userOnBoardDispatch],
	)

	const debouncedFetchSkills = useCallback(
		_.debounce((searchTerm: any, callback: any) => {
			refetchSkills({ key: searchTerm })
				.then((result: any) => {
					return callback(
						result.data.skillCollection.edges?.map((item: any) => {
							let tempItem = {
								value: '',
								label: '',
							}
							tempItem.value = item.node.id
							tempItem.label = item.node.title
							return tempItem
						}),
					)
				})
				.catch((error: any) => callback(error, null))
		}, 500),
		[],
	)

	const getSkillsOptions = useCallback(
		(input: any, callback: any) => {
			if (!input) {
				return Promise.resolve({ options: [] })
			}
			debouncedFetchSkills(input, callback)
			// return Promise.resolve({ options: [{value: 1, label: 'Skill 1'}, {value: 2, label: 'Skill 2'}] })
		},
		[debouncedFetchSkills],
	)

	return (
		<div className={classes.mt20}>
			<Grid container direction={'row'} spacing={2}>
				<Grid item xs={12} className={classes.fieldContainer}>
					<div
						className={classNames(classes.labelField, {
							[classes.labelFieldError]: error.skills,
						})}
					>
						{t('Add Skills. Search from our database ...')}
						<span className={classes.requiredField}>
							{`(${t('Required')})`}
						</span>
					</div>
					<SearchSelect
						handleChange={handleChangeMultiSkills}
						// defaultOptions={defaultSkillsList}
						loadOptions={getSkillsOptions}
						items={skills}
						placeholderText={t('Search for skills...')}
						isMulti={true}
					/>
				</Grid>

				<Grid item xs={12} className={classes.fieldContainer}>
					<div
						className={classNames(classes.labelField, {
							[classes.labelFieldError]: error.values,
						})}
					>
						{t('Add values')}
						<span
							className={classNames(classes.requiredField, {
								[classes.labelFieldError]: error.values,
							})}
						>
							{`(${t('Required')})`}
						</span>
					</div>
					<Grid container={true} direction="row">
						<TextField
							id="standard-full-width"
							style={{ margin: '8px 0px' }}
							placeholder={t('Your values, go here')}
							helperText="comma separated list of values"
							fullWidth
							margin="normal"
							FormHelperTextProps={{
								classes: { root: classes.helperTextInfo },
							}}
							InputProps={{
								classes: { input: classes.textFieldInput },
							}}
							InputLabelProps={{
								shrink: true,
							}}
							value={values}
							onChange={handleChangeValues}
						/>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<div className={classes.btnsContainer}>
						<Button
							onClick={handleBack}
							// className={classes.button}
						>
							{t('Back')}
						</Button>
						<Fab
							onClick={goToNextStep}
							className={classes.nextBtn}
							variant="extended"
							size="medium"
							color="primary"
							aria-label="Add"
						>
							{t('Next') || ''}
						</Fab>
					</div>
				</Grid>
			</Grid>
		</div>
	)
}
const Step2WithStoreValue = (props: any) => (
	<StoreValue keys={['user.id|storeUserId']}>
		<Step2View {...props} />
	</StoreValue>
)
const Step2 = memo((props: any) => (
	<UserOnboardingStateValue
		keys={['id', 'userOnBoardDispatch', 'skills', 'values', 'error']}
	>
		<Step2WithStoreValue {...props} />
	</UserOnboardingStateValue>
))

export default Step2
